/**
 * Pure math helpers for analyzing a user's continuous-location point history.
 * No React, no I/O — fully unit-testable.
 *
 * Point shape (from varna-server):
 *   { id, user_id, latitude, longitude, accuracy, altitude, heading, speed,
 *     recorded_at, created_at }
 */

const EARTH_RADIUS_M = 6371e3;
const DEG_TO_RAD = Math.PI / 180;

/**
 * Great-circle distance between two lat/lng pairs in meters (haversine).
 */
export const haversineMeters = (a, b) => {
  if (!a || !b) return 0;
  const lat1 = a.latitude * DEG_TO_RAD;
  const lat2 = b.latitude * DEG_TO_RAD;
  const dLat = (b.latitude - a.latitude) * DEG_TO_RAD;
  const dLng = (b.longitude - a.longitude) * DEG_TO_RAD;

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Ensure points are sorted ascending by recorded_at. Mutates a copy.
 */
export const sortByRecordedAt = (points) =>
  [...(points ?? [])].sort(
    (a, b) =>
      new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
  );

/**
 * Compute aggregate summary stats for a point series.
 *   - totalDistanceMeters : cumulative path length (haversine between consecutive points)
 *   - durationMs          : last.recorded_at - first.recorded_at
 *   - averageSpeedKmh     : distance / duration, well-defined only if duration > 0
 *   - topSpeedKmh         : max of (reported speed or segment-derived speed)
 *   - first / last        : endpoints
 *   - accuracyMedianM     : rough quality indicator
 */
export const summarizeMovements = (rawPoints) => {
  const points = sortByRecordedAt(rawPoints);
  const count = points.length;

  if (count === 0) {
    return {
      count: 0,
      totalDistanceMeters: 0,
      durationMs: 0,
      averageSpeedKmh: 0,
      topSpeedKmh: 0,
      first: null,
      last: null,
      accuracyMedianM: null,
    };
  }

  let totalDistanceMeters = 0;
  let topSpeedKmh = 0;
  const accuracies = [];

  for (let i = 0; i < count; i++) {
    const curr = points[i];
    if (typeof curr.accuracy === "number" && !Number.isNaN(curr.accuracy)) {
      accuracies.push(curr.accuracy);
    }
    if (typeof curr.speed === "number" && curr.speed > 0) {
      const kmh = curr.speed * 3.6;
      if (kmh > topSpeedKmh) topSpeedKmh = kmh;
    }
    if (i > 0) {
      const prev = points[i - 1];
      const segMeters = haversineMeters(prev, curr);
      totalDistanceMeters += segMeters;

      const segMs =
        new Date(curr.recorded_at).getTime() -
        new Date(prev.recorded_at).getTime();
      if (segMs > 0) {
        const derivedKmh = (segMeters / segMs) * 1000 * 3.6;
        if (derivedKmh > topSpeedKmh) topSpeedKmh = derivedKmh;
      }
    }
  }

  const first = points[0];
  const last = points[count - 1];
  const durationMs =
    new Date(last.recorded_at).getTime() -
    new Date(first.recorded_at).getTime();

  const averageSpeedKmh =
    durationMs > 0 ? (totalDistanceMeters / durationMs) * 1000 * 3.6 : 0;

  accuracies.sort((x, y) => x - y);
  const accuracyMedianM =
    accuracies.length > 0
      ? accuracies[Math.floor(accuracies.length / 2)]
      : null;

  return {
    count,
    totalDistanceMeters,
    durationMs,
    averageSpeedKmh,
    topSpeedKmh,
    first,
    last,
    accuracyMedianM,
  };
};

/**
 * Detect "stops" — contiguous runs of points where the user stayed within
 * `radiusMeters` of the first point of the run for at least `minDwellMs`.
 * A stop is the forensic unit that analysts care about ("where did they
 * linger, and for how long?").
 *
 * Returns an array of { startIndex, endIndex, startAt, endAt, dwellMs,
 * center: { latitude, longitude }, pointCount }.
 */
export const detectStops = (
  rawPoints,
  { radiusMeters = 35, minDwellMs = 2 * 60 * 1000 } = {}
) => {
  const points = sortByRecordedAt(rawPoints);
  const stops = [];
  if (points.length < 2) return stops;

  let anchorIdx = 0;
  let lastInsideIdx = 0;

  const finalizeStopIfQualifies = (anchor, startIdx, endIdx) => {
    const startAt = new Date(points[startIdx].recorded_at).getTime();
    const endAt = new Date(points[endIdx].recorded_at).getTime();
    const dwellMs = endAt - startAt;
    if (dwellMs >= minDwellMs) {
      // Average the cluster to smooth GPS jitter in the "stop" marker.
      let sumLat = 0;
      let sumLng = 0;
      for (let k = startIdx; k <= endIdx; k++) {
        sumLat += points[k].latitude;
        sumLng += points[k].longitude;
      }
      const n = endIdx - startIdx + 1;
      stops.push({
        startIndex: startIdx,
        endIndex: endIdx,
        startAt,
        endAt,
        dwellMs,
        center: { latitude: sumLat / n, longitude: sumLng / n },
        pointCount: n,
      });
    }
  };

  for (let i = 1; i < points.length; i++) {
    const d = haversineMeters(points[anchorIdx], points[i]);
    if (d <= radiusMeters) {
      lastInsideIdx = i;
      continue;
    }
    // Left the radius — close the current cluster.
    finalizeStopIfQualifies(points[anchorIdx], anchorIdx, lastInsideIdx);
    anchorIdx = i;
    lastInsideIdx = i;
  }
  // Flush the trailing cluster.
  finalizeStopIfQualifies(
    points[anchorIdx],
    anchorIdx,
    lastInsideIdx
  );

  return stops;
};

/**
 * Split the point series into "segments" around detected stops so the map
 * can draw separate polylines for each moving leg. Stops themselves are
 * rendered as markers rather than polyline vertices.
 */
export const splitIntoSegments = (rawPoints, stops) => {
  const points = sortByRecordedAt(rawPoints);
  if (points.length === 0) return [];
  if (!stops || stops.length === 0) return [points];

  const segments = [];
  let cursor = 0;
  for (const stop of stops) {
    if (stop.startIndex > cursor) {
      segments.push(points.slice(cursor, stop.startIndex + 1));
    }
    cursor = stop.endIndex;
  }
  if (cursor < points.length - 1) {
    segments.push(points.slice(cursor, points.length));
  }
  return segments.filter((seg) => seg.length >= 2);
};

/**
 * Format a duration in ms to a human-readable Swedish string.
 */
export const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h} tim ${m} min`;
  if (m > 0) return `${m} min ${s} s`;
  return `${s} s`;
};

/**
 * Format distance in meters to km or m with 1 decimal.
 */
export const formatDistance = (meters) => {
  if (!Number.isFinite(meters) || meters < 0) return "—";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

export const formatKmh = (value) => {
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value < 1) return "< 1 km/h";
  return `${value.toFixed(1)} km/h`;
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};
